import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  Query,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

import { ThemeProvider } from '@kognitos/lattice';
import { AuthProvider, AxiosInterceptorProvider } from '@/shared/auth';
import { ConfigProvider } from '@/config/ConfigProvider';

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // When you're using meta.onSuccess or meta.onError,
    // Make sure you always set refetchOnMount to 'always'
    // Otherwise, this query will not call onSuccess or onError
    // As, these will be only called on API success,
    // not on generated Hook's initial call
    // These will help us to avoid using an extra
    // `useEffect` to call the onSuccess or onError
    onSuccess: (
      data: unknown,
      query: Query<unknown, unknown, unknown, readonly unknown[]>,
    ) => {
      if (query.meta?.onSuccess && typeof query.meta.onSuccess === 'function') {
        query.meta.onSuccess(data, query);
      }
    },
    onError: (
      error: unknown,
      query: Query<unknown, unknown, unknown, readonly unknown[]>,
    ) => {
      if (query.meta?.onError && typeof query.meta.onError === 'function') {
        query.meta.onError(error, query);
      }
    },
  }),
});

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: QueryClient;
  }
}

window.__TANSTACK_QUERY_CLIENT__ = queryClient;

export const Providers = ({ children }: { children: React.ReactNode }) => {
  return (
    <ConfigProvider>
      <AuthProvider>
        <AxiosInterceptorProvider>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools
              buttonPosition="bottom-right"
              initialIsOpen={false}
            />
            <ThemeProvider defaultTheme="dark" storageKey="kognitos-ui-theme">
              {children}
            </ThemeProvider>
          </QueryClientProvider>
        </AxiosInterceptorProvider>
      </AuthProvider>
    </ConfigProvider>
  );
};
